import { useMemo } from 'react'

import { useAuth } from '../../auth/authContext'
import { ApiClient } from '../../data/api'
import type { RouteAnswer } from '../../lib/route'
import type { RouteStep, RouteTunnel } from '../../lib/tulipSuggest'
import type { LatLon } from '../../lib/types'

/** Holt eine Strecke von einem Punkt über die Zwischenpunkte zu einem anderen. */
export type FetchRoute = (
  from: LatLon,
  via: LatLon[],
  to: LatLon,
  signal?: AbortSignal,
) => Promise<RouteAnswer>

/**
 * Der Zugang zum Routendienst, an die laufende Anmeldung gebunden.
 *
 * Ein eigener Client und nicht der des Abgleichs: der steckt in der Engine im
 * RoadbookProvider und hat dort nichts nach draussen zu geben. ApiClient hält
 * ausser der Tokenquelle keinen Zustand, ein zweiter kostet also nichts, und
 * die Streckenberechnung bleibt damit ganz in ihrem eigenen Bereich - der
 * Datenschicht muss dafür nichts hinzugefügt werden.
 */
export function useFetchRoute(): FetchRoute {
  const { getToken } = useAuth()
  return useMemo(() => {
    const client = new ApiClient(getToken)
    return (from, via, to, signal) => client.fetchRoute(from, via, to, signal)
  }, [getToken])
}

/**
 * Das Rohmaterial für die Tulpenvorschläge, so wie der Server es liefert.
 *
 * Zwei Quellen in einer Antwort: die Manöver kommen vom Routendienst, die
 * Tunnel aus dem Kachelarchiv des Servers. Für den Aufrufer ist es eine
 * Anfrage, und das ist keine Bequemlichkeit - der Routendienst erlaubt eine
 * Anfrage je Sekunde, und die Tunnel hängen an genau der Geometrie, die diese
 * Anfrage ohnehin zurückbringt.
 *
 * Was hier fehlt, sind die Kehren: die stehen schon in der Etappe. Sie wurden
 * gezählt, als die Strecke berechnet wurde, denn danach ist es zu spät - die
 * gespeicherte Linie ist auf 400 Punkte ausgedünnt.
 */
export interface TulipSource {
  steps: RouteStep[]
  /**
   * Fehlt, wenn der Server kein Kartenarchiv hat. Dann gibt es keine Aussage
   * über Tunnel - was etwas anderes ist als die Aussage, es gebe keine.
   */
  tunnels?: { coverage: number; found: RouteTunnel[] }
}

/** Holt Manöver und Tunnel derselben Strecke. */
export type FetchTulipSource = (
  from: LatLon,
  via: LatLon[],
  to: LatLon,
  signal?: AbortSignal,
) => Promise<TulipSource>

/**
 * Der Zugang zum Rohmaterial der Vorschläge.
 *
 * Derselbe Endpunkt und dieselbe Berechnung wie bei useFetchRoute, nur mit der
 * Bitte um die Abbiegeanweisungen. Beim Routendienst kostet das nichts - er
 * liefert sie in derselben Antwort -, und ist die Strecke schon einmal mit
 * ihnen berechnet worden, kommt alles aus dem Zwischenspeicher des Servers.
 * Eine eigene Anfrage beim fremden Dienst entsteht nur für Strecken, die aus
 * der Zeit vor dieser Erweiterung stammen, und für jede genau einmal. Die
 * Tunnelsuche kostet gar keine fremde Anfrage: sie liest eine Datei, die auf
 * demselben Rechner liegt.
 *
 * Herausgereicht wird nur das Rohmaterial: was die Etappe an Strecke, Länge und
 * Fahrzeit gespeichert hat, ist längst entschieden und wird von einem Blick auf
 * die Kreuzungen nicht neu verhandelt.
 */
export function useFetchTulipSource(): FetchTulipSource {
  const { getToken } = useAuth()
  return useMemo(() => {
    const client = new ApiClient(getToken)
    return async (from, via, to, signal) => {
      const antwort = await client.fetchRoute(from, via, to, signal, true)
      return {
        steps: antwort.steps ?? [],
        ...(antwort.tunnels ? { tunnels: antwort.tunnels } : {}),
      }
    }
  }, [getToken])
}
